import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    getCandidate,
    updateCandidate,
    getJobOffers,
    getSkills,
    createSkill,
    updateCandidateSkills,
    createInterview,
    uploadCandidateCv,
    getCandidateCvUrl,
} from "../../../_api/Talent";
import { SideBar } from "../../../_helpers/components/SideBar";
import { getUsers } from "../../../_api/Admin";
import { BackButton } from "../../../_helpers/components/BackButton";

const LEVEL_LABELS = { DEBUTANT: "Débutant", INTERMEDIAIRE: "Intermédiaire", AVANCE: "Avancé", EXPERT: "Expert" };
const STATUS_LABELS = { NEW: "Nouveau", SCREENING: "Présélection", INTERVIEW: "Entretien", OFFER: "Offre", HIRED: "Embauché", REJECTED: "Rejeté", WAITING: "En attente", ACCEPTED: "Accepté" };
const STATUS_COLORS = { NEW: "bg-slate-100 text-slate-800", SCREENING: "bg-blue-100 text-blue-800", INTERVIEW: "bg-amber-100 text-amber-800", OFFER: "bg-emerald-100 text-emerald-800", HIRED: "bg-green-100 text-green-800", REJECTED: "bg-red-100 text-red-800", WAITING: "bg-gray-100 text-gray-800", ACCEPTED: "bg-teal-100 text-teal-800" };
const INTERVIEW_TYPE_LABELS = { ANNUEL: "Annuel", PROFESSIONNEL: "Professionnel", EVAL_360: "Évaluation 360", RECRUTEMENT: "Recrutement" };

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");

const LinkIcon = ({ onClick, title }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className="p-1.5 rounded text-gray-400 hover:text-app-primary hover:bg-gray-100 transition-colors"
    >
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
    </button>
);

const AddIcon = ({ onClick, title }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className="p-1.5 rounded text-gray-400 hover:text-app-primary hover:bg-gray-100 transition-colors"
    >
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
    </button>
);

const SectionCard = ({ title, subtitle, iconPath, gradient, children, compact, headerAction }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className={`${compact ? "p-3" : "p-4"} border-b border-gray-200 flex items-center justify-between`}>
            <div className="flex items-center space-x-2">
                <div className={`flex items-center justify-center ${compact ? "w-8 h-8" : "w-9 h-9"} bg-gradient-to-br ${gradient} rounded-lg shrink-0`}>
                    <svg className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                    </svg>
                </div>
                <div>
                    <h2 className={`${compact ? "text-base" : "text-lg"} font-bold text-gray-900`}>{title}</h2>
                    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                </div>
            </div>
            {headerAction}
        </div>
        <div className={compact ? "p-3" : "p-4"}>{children}</div>
    </div>
);

export const CandidateDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [candidate, setCandidate] = useState(null);
    const [offers, setOffers] = useState([]);
    const [allSkills, setAllSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editInfo, setEditInfo] = useState(false);
    const [infoForm, setInfoForm] = useState({});
    const [commentsForm, setCommentsForm] = useState("");
    const [savingComments, setSavingComments] = useState(false);
    const [skillForm, setSkillForm] = useState({ talent_skill_id: "", level: "INTERMEDIAIRE" });
    const [showSkillModal, setShowSkillModal] = useState(false);
    const [showAddInterviewModal, setShowAddInterviewModal] = useState(false);
    const [interviewForm, setInterviewForm] = useState({ type: "RECRUTEMENT", date: "", time: "" });
    const [cvUploading, setCvUploading] = useState(false);
    const [users, setUsers] = useState([]);
    const [newSkillForm, setNewSkillForm] = useState({ name: "", type: "TECHNIQUE", level: "INTERMEDIAIRE" });

    const fetch = () => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            getCandidate(id).catch((err) => {
                if (err?.response?.status === 404) return null;
                throw err;
            }),
            getJobOffers().catch(() => ({ data: [] })),
            getSkills().catch(() => ({ data: [] })),
            getUsers().catch(() => ({ data: [] })),
        ])
            .then(([cRes, oRes, sRes, uRes]) => {
                const c = cRes?.data || cRes;
                if (c) {
                    setCandidate(c);
                    setInfoForm({
                        first_name: c.first_name || "",
                        last_name: c.last_name || "",
                        email: c.email || "",
                        phone: c.phone || "",
                        job_offer_id: c.job_offer_id || "",
                        status: c.status || "",
                    });
                    setCommentsForm(c.comments || "");
                } else {
                    setCandidate(null);
                }
                setOffers(oRes.data || []);
                setAllSkills(sRes.data || []);
                setUsers((uRes.data || []).filter((u) => u.activated !== false));
            })
            .catch((err) => setError(err?.response?.data?.message || "Erreur"))
            .finally(() => setLoading(false));
    };

    useEffect(() => fetch(), [id]);

    const handleSaveInfo = (e) => {
        e.preventDefault();
        updateCandidate(id, { ...infoForm, job_offer_id: infoForm.job_offer_id || null })
            .then(() => {
                toast.success("Informations mises à jour.");
                setEditInfo(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleSaveComments = () => {
        setSavingComments(true);
        updateCandidate(id, { comments: commentsForm })
            .then(() => {
                toast.success("Commentaires enregistrés.");
                setCandidate((c) => (c ? { ...c, comments: commentsForm } : null));
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
            .finally(() => setSavingComments(false));
    };

    const skills = candidate?.talentSkills || [];
    const interviews = candidate?.interviews || [];
    const availableSkills = allSkills.filter((s) => !skills.some((us) => us.id === s.id));

    const handleAddSkill = (e) => {
        e.preventDefault();
        if (!skillForm.talent_skill_id) return;
        const current = skills.map((s) => ({ talent_skill_id: s.id, level: s.pivot?.level || "INTERMEDIAIRE" }));
        const updated = [...current, { talent_skill_id: parseInt(skillForm.talent_skill_id), level: skillForm.level }];
        updateCandidateSkills(id, updated)
            .then(() => {
                toast.success("Compétence ajoutée.");
                setSkillForm({ talent_skill_id: "", level: "INTERMEDIAIRE" });
                setShowSkillModal(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleCreateAndAssociateSkill = (e) => {
        e.preventDefault();
        if (!newSkillForm.name?.trim()) return;
        createSkill({ name: newSkillForm.name.trim(), type: newSkillForm.type })
            .then((res) => {
                const newSkill = res.data?.data || res.data;
                const skillId = newSkill?.id;
                if (!skillId) {
                    toast.error("Erreur lors de la création.");
                    return;
                }
                const current = skills.map((s) => ({ talent_skill_id: s.id, level: s.pivot?.level || "INTERMEDIAIRE" }));
                const updated = [...current, { talent_skill_id: skillId, level: newSkillForm.level }];
                return updateCandidateSkills(id, updated);
            })
            .then(() => {
                toast.success("Compétence créée et associée.");
                setNewSkillForm({ name: "", type: "TECHNIQUE", level: "INTERMEDIAIRE" });
                setShowSkillModal(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleRemoveSkill = (skillId) => {
        const updated = skills.filter((s) => s.id !== skillId).map((s) => ({ talent_skill_id: s.id, level: s.pivot?.level || "INTERMEDIAIRE" }));
        updateCandidateSkills(id, updated)
            .then(() => {
                toast.success("Compétence retirée.");
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleAddInterview = (e) => {
        e.preventDefault();
        if (!interviewForm.date) return;
        createInterview({
            candidate_id: parseInt(id),
            type: interviewForm.type,
            date: interviewForm.date,
            time: interviewForm.time || undefined,
            evaluator_id: interviewForm.evaluator_id || undefined,
        })
            .then(() => {
                toast.success("Entretien créé.");
                setInterviewForm({ type: "RECRUTEMENT", date: "", time: "" });
                setShowAddInterviewModal(false);
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"));
    };

    const handleCvUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/\.(pdf|jpg|jpeg|png)$/i.test(file.name)) {
            toast.error("Format accepté : PDF, JPG, PNG.");
            return;
        }
        setCvUploading(true);
        const formData = new FormData();
        formData.append("cv", file);
        uploadCandidateCv(id, formData)
            .then(() => {
                toast.success("CV enregistré.");
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
            .finally(() => {
                setCvUploading(false);
                e.target.value = "";
            });
    };

    const handleViewCv = () => {
        getCandidateCvUrl(id)
            .then((res) => {
                const url = res.data?.url;
                if (url) window.open(url, "_blank");
                else toast.error("CV introuvable.");
            })
            .catch((err) => toast.error(err?.response?.data?.message || "CV introuvable."));
    };

    if (loading) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-app-primary mx-auto mb-3"></div>
                    <p className="text-sm text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error || !candidate) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center">
                <div className="text-center text-red-600 text-sm">{error || "Candidat introuvable"}</div>
                <button onClick={() => navigate("/talents/recrutement")} className="mt-2 text-sm text-app-primary hover:underline">
                    Retour au recrutement
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <BackButton
                                onClick={() => navigate("/talents/recrutement")}
                                title="Retour"
                            />
                            <div className="flex items-center space-x-2 min-w-0">
                                <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-600 rounded-full text-white font-semibold text-sm shrink-0">
                                    {candidate.first_name?.[0]}{candidate.last_name?.[0]}
                                </div>
                                <div className="min-w-0">
                                    <h1 className="text-xl font-bold text-gray-900 truncate">{candidate.first_name} {candidate.last_name}</h1>
                                    <p className="text-xs text-gray-500 truncate">{candidate.email} · {candidate.job_offer?.title || "Sans offre"}</p>
                                    <span className={`inline-flex mt-1 px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[candidate.status] || "bg-gray-100 text-gray-800"}`}>
                                        {STATUS_LABELS[candidate.status] || candidate.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <label className="cursor-pointer">
                                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleCvUpload} disabled={cvUploading} className="hidden" />
                                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 hover:border-app-primary hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors">
                                    {cvUploading ? (
                                        <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                    )}
                                    {candidate.cv_path ? "Remplacer le CV" : "Ajouter le CV"}
                                </span>
                            </label>
                            {candidate.cv_path && (
                                <button onClick={handleViewCv} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-app-primary bg-app-primary text-white hover:bg-app-secondary text-sm font-medium transition-colors">
                                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Voir le CV
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-4">
                        <SectionCard title="Informations" iconPath="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" gradient="from-slate-500 to-slate-600" compact>
                            {editInfo ? (
                                <form onSubmit={handleSaveInfo} className="space-y-3">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Prénom</label>
                                            <input value={infoForm.first_name} onChange={(e) => setInfoForm((f) => ({ ...f, first_name: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" required />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Nom</label>
                                            <input value={infoForm.last_name} onChange={(e) => setInfoForm((f) => ({ ...f, last_name: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" required />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Email</label>
                                        <input type="email" value={infoForm.email} onChange={(e) => setInfoForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" required />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Téléphone</label>
                                        <input value={infoForm.phone} onChange={(e) => setInfoForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Offre</label>
                                        <select value={infoForm.job_offer_id} onChange={(e) => setInfoForm((f) => ({ ...f, job_offer_id: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded">
                                            <option value="">Sans offre</option>
                                            {offers.map((o) => (
                                                <option key={o.id} value={o.id}>{o.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Statut</label>
                                        <select value={infoForm.status} onChange={(e) => setInfoForm((f) => ({ ...f, status: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded">
                                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                                <option key={k} value={k}>{v}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex gap-2 pt-2">
                                        <button type="submit" className="px-2 py-1 text-xs font-medium rounded bg-app-primary text-white">Enregistrer</button>
                                        <button type="button" onClick={() => setEditInfo(false)} className="px-2 py-1 text-xs border rounded text-gray-600">Annuler</button>
                                    </div>
                                </form>
                            ) : (
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div><p className="text-gray-500 text-xs">Prénom</p><p className="font-medium">{candidate.first_name}</p></div>
                                    <div><p className="text-gray-500 text-xs">Nom</p><p className="font-medium">{candidate.last_name}</p></div>
                                    <div><p className="text-gray-500 text-xs">Email</p><p className="font-medium">{candidate.email}</p></div>
                                    <div><p className="text-gray-500 text-xs">Téléphone</p><p className="font-medium">{candidate.phone || "-"}</p></div>
                                    <div><p className="text-gray-500 text-xs">Offre</p><p className="font-medium">{candidate.job_offer?.title || "-"}</p></div>
                                    <div><p className="text-gray-500 text-xs">Statut</p><p className="font-medium">{STATUS_LABELS[candidate.status] || candidate.status}</p></div>
                                    <div className="col-span-2 pt-2">
                                        <button onClick={() => setEditInfo(true)} className="text-xs text-app-primary hover:underline">Modifier</button>
                                    </div>
                                </div>
                            )}
                        </SectionCard>

                        <SectionCard title="Commentaires" iconPath="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" gradient="from-indigo-500 to-indigo-600" compact>
                            <textarea
                                value={commentsForm}
                                onChange={(e) => setCommentsForm(e.target.value)}
                                onBlur={handleSaveComments}
                                rows={4}
                                placeholder="Notes, impressions, suivi du candidat..."
                                className="w-full px-2 py-1.5 text-sm border rounded resize-none"
                            />
                            {savingComments && <p className="text-xs text-gray-500 mt-1">Enregistrement...</p>}
                        </SectionCard>

                        <SectionCard
                            title="Compétences"
                            subtitle={skills.length ? `${skills.length} compétence${skills.length > 1 ? "s" : ""}` : "Aucune"}
                            iconPath="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                            gradient="from-blue-500 to-blue-600"
                            compact
                            headerAction={<LinkIcon onClick={() => setShowSkillModal(true)} title="Associer une compétence" />}
                        >
                            {skills.length > 0 && (
                                <ul className="space-y-1">
                                    {skills.map((s) => (
                                        <li key={s.id} className="flex justify-between items-center py-1.5 px-2 rounded bg-gray-50 text-sm">
                                            <span>{s.name} <span className="text-gray-500">– {LEVEL_LABELS[s.pivot?.level] || s.pivot?.level}</span></span>
                                            <button onClick={() => handleRemoveSkill(s.id)} className="text-xs text-red-600 hover:text-red-800">Retirer</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(!skills.length || availableSkills.length > 0) && (
                                <p className="text-xs text-gray-500 mt-2">
                                    {availableSkills.length > 0 ? "Cliquez sur l'icône lien pour associer une compétence." : skills.length > 0 ? "Toutes les compétences sont déjà associées." : "Aucune compétence associée."}
                                </p>
                            )}
                        </SectionCard>
                    </div>

                    <div className="space-y-4">
                        <SectionCard
                            title="Entretiens"
                            subtitle={interviews.length ? `${interviews.length} entretien${interviews.length > 1 ? "s" : ""}` : "Aucun"}
                            iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            gradient="from-emerald-500 to-emerald-600"
                            compact
                            headerAction={<AddIcon onClick={() => setShowAddInterviewModal(true)} title="Ajouter un entretien" />}
                        >
                            {interviews.length > 0 ? (
                                <ul className="space-y-2">
                                    {interviews.map((i) => (
                                        <li
                                            key={i.id}
                                            onClick={() => navigate(`/talents/entretiens/${i.id}`)}
                                            className="p-3 rounded-lg bg-gray-50 border border-gray-100 text-sm cursor-pointer hover:bg-gray-100 transition-colors flex justify-between items-center"
                                        >
                                            <div>
                                                <p className="font-medium text-gray-900">{INTERVIEW_TYPE_LABELS[i.type] || i.type || "Entretien"}</p>
                                                <p className="text-gray-600">Date : {formatDate(i.date)}{i.evaluator ? ` · ${i.evaluator.first_name} ${i.evaluator.last_name}` : ""}</p>
                                            </div>
                                            <span className="text-xs text-app-primary shrink-0">Voir →</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-500">Aucun entretien planifié pour ce candidat.</p>
                            )}
                        </SectionCard>
                    </div>
                </div>
            </div>

            <SideBar
                title="Associer une compétence"
                content={
                    <div className="space-y-6">
                        {availableSkills.length > 0 && (
                            <form onSubmit={handleAddSkill} className="space-y-4">
                                <p className="text-sm text-gray-600">Choisissez une compétence du référentiel à associer au candidat.</p>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Compétence existante</label>
                                    <select
                                        value={skillForm.talent_skill_id}
                                        onChange={(e) => setSkillForm((f) => ({ ...f, talent_skill_id: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        required
                                    >
                                        <option value="">— Choisir —</option>
                                        {availableSkills.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
                                    <select
                                        value={skillForm.level}
                                        onChange={(e) => setSkillForm((f) => ({ ...f, level: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {Object.entries(LEVEL_LABELS).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <button type="submit" className="px-4 py-2 rounded border-2 border-app-primary bg-app-primary text-white font-medium">Associer</button>
                                </div>
                            </form>
                        )}

                        <div className="pt-4 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-3">Ou créer une nouvelle compétence</p>
                            <form onSubmit={handleCreateAndAssociateSkill} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Nom</label>
                                    <input
                                        type="text"
                                        value={newSkillForm.name}
                                        onChange={(e) => setNewSkillForm((f) => ({ ...f, name: e.target.value }))}
                                        placeholder="Ex: Excel, Communication"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Type</label>
                                    <select
                                        value={newSkillForm.type}
                                        onChange={(e) => setNewSkillForm((f) => ({ ...f, type: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="TECHNIQUE">Technique</option>
                                        <option value="COMPORTEMENTALE">Comportementale</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Niveau</label>
                                    <select
                                        value={newSkillForm.level}
                                        onChange={(e) => setNewSkillForm((f) => ({ ...f, level: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {Object.entries(LEVEL_LABELS).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <button type="submit" className="px-4 py-2 rounded border-2 border-app-primary bg-app-primary text-white font-medium">Créer et associer</button>
                                </div>
                            </form>
                        </div>

                        {availableSkills.length === 0 && !newSkillForm.name && (
                            <p className="text-sm text-gray-500">Toutes les compétences existantes sont déjà associées. Créez-en une nouvelle ci-dessous.</p>
                        )}

                        <div className="pt-2">
                            <button type="button" onClick={() => setShowSkillModal(false)} className="text-sm text-gray-500 hover:text-gray-700">Fermer</button>
                        </div>
                    </div>
                }
                isOpen={showSkillModal}
                onClose={() => setShowSkillModal(false)}
            />

            <SideBar
                title="Ajouter un entretien"
                content={
                    <form onSubmit={handleAddInterview} className="space-y-4">
                        <p className="text-sm text-gray-600">Planifier un entretien de recrutement pour ce candidat.</p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select
                                value={interviewForm.type}
                                onChange={(e) => setInterviewForm((f) => ({ ...f, type: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                                {Object.entries(INTERVIEW_TYPE_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                            <input
                                type="date"
                                value={interviewForm.date}
                                onChange={(e) => setInterviewForm((f) => ({ ...f, date: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Heure</label>
                            <input
                                type="time"
                                value={interviewForm.time}
                                onChange={(e) => setInterviewForm((f) => ({ ...f, time: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Évaluateur</label>
                            <select
                                value={interviewForm.evaluator_id || ""}
                                onChange={(e) => setInterviewForm((f) => ({ ...f, evaluator_id: e.target.value || undefined }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                                <option value="">— Choisir —</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button type="submit" className="px-4 py-2 rounded border-2 border-app-primary bg-app-primary text-white font-medium">
                                Créer l'entretien
                            </button>
                            <button type="button" onClick={() => setShowAddInterviewModal(false)} className="px-4 py-2 border rounded text-gray-600">
                                Annuler
                            </button>
                        </div>
                    </form>
                }
                isOpen={showAddInterviewModal}
                onClose={() => setShowAddInterviewModal(false)}
            />
        </div>
    );
};
